/**
 * Groepssleutel voor één ingevulde vragenlijst: FormID wanneer aanwezig, anders
 * de milliseconde. Geexporteerd zodat afnemers die alleen willen TELLEN (zoals
 * de retentiesignalen) niet hun eigen kopie van deze regel maken.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * @param {string} formID - FormID van het antwoord ('' wanneer afwezig)
 * @param {number} ms - Tijdstempel van het antwoord in milliseconden
 * @returns {string} Sleutel die nooit botst tussen beide vormen
 */
export const formGroupKey = (formID, ms) => formID || `ts:${ms}`;

/**
 * @typedef {object} FormGroup
 * @property {string} key - Unique key for this group (FormID, or 'ts:<ms>' for legacy groups)
 * @property {string} formID - FormID shared by the group's responses ('' for legacy responses without one)
 * @property {Date} date - Earliest answer time in the group
 * @property {string} momentID - MomentID of the group's responses
 * @property {string} sessionID - SessionID of the group's responses
 * @property {Array<object>} items - The raw response docs in this group
 */

/**
 * Groups questionnaire responses into submitted forms.
 *
 * One filled-in questionnaire = one FormID (every save path stamps the
 * respondent's session id on each answer), so FormID is the grouping key.
 * Grouping by timestamp undercounts: batch-written data can give several
 * respondents the exact same millisecond, merging them into one form.
 * AI-CONTEXT: See docs/pitfalls.md#database-responses-grouped-by-millisecond-timestamp
 *
 * Legacy responses without a FormID fall back to millisecond-timestamp
 * grouping, keyed 'ts:<ms>' so they can never collide with a FormID group.
 *
 * @param {Array<object> | null | undefined} results - QuestionnairesResponses docs
 * @returns {Array<FormGroup>} Form groups, newest first
 */
const groupResponsesIntoForms = (results) => {
    const grouped = {};

    (results || []).forEach((item) => {
        const date = item.Timestamp.toDate();
        const formID = item.FormID || '';
        const key = formGroupKey(formID, date.getTime());

        if (!grouped[key]) {
            grouped[key] = {
                key,
                formID,
                date,
                momentID: item.MomentID,
                sessionID: item.SessionID,
                items: [],
            };
        } else if (date.getTime() < grouped[key].date.getTime()) {
            grouped[key].date = date;
        }

        grouped[key].items.push(item);
    });

    return Object.values(grouped).sort((a, b) => b.date.getTime() - a.date.getTime());
};

export default groupResponsesIntoForms;
